import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { timesheetCollectionNew } from '../../../services/firebase';
import { fetchProjects } from '../../../services/projects-services';

const PAID_LEAVE_FLOW_ID = '20015';
const PAID_LEAVE_PROJECT_NAME = 'FKA Paid Personal Leave';
const NON_BILLABLE_PROJECT_STATES = ['Opportunity', 'Committed'];

const round2 = (value) => Number((Number(value) || 0).toFixed(2));

const toBool = (value) => {
  if (value === true) return true;
  if (value === false || value === undefined || value === null) return false;
  if (typeof value === 'number') return value !== 0;
  const str = String(value).trim().toLowerCase();
  return str !== '' && str !== '0' && str !== '0.00' && str !== 'false';
};

const parseEntryDate = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = String(dateStr).split('-');
  if (!day || !month || !year) return null;
  return new Date(`${year}-${month}-${day}`);
};

const getProjectName = (entry, projectFromMap) => {
  return (
    entry.projectName ||
    entry.name ||
    projectFromMap?.projectName ||
    projectFromMap?.builderData?.name ||
    ''
  );
};

const getTimesheetDocId = (user) => {
  if (!user) return '';
  if (user.firstName && user.surname) {
    return `${user.firstName}_${user.surname}`.replace(/\s+/g, '_').toLowerCase();
  }
  if (user.fullName) {
    return user.fullName.replace(/\s+/g, '_').toLowerCase();
  }
  return String(user.id || '').toLowerCase();
};

const fetchUserWeeks = async (timesheetDocId) => {
  const snapshot = await timesheetCollectionNew
    .doc(timesheetDocId)
    .collection('weeks')
    .get();
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// Flatten weeks → array of normalized rows with classification metadata.
const buildEnrichedRows = ({ weeks, projectsMap, startDate, endDate, isContractUser }) => {
  const rangeStart = dayjs(startDate).startOf('day').toDate();
  const rangeEnd = dayjs(endDate).endOf('day').toDate();
  const rows = [];

  weeks.forEach((week) => {
    const weekStarting = week.id;
    const weekDate = new Date(weekStarting);
    const weekIncluded =
      !Number.isNaN(weekDate.getTime()) && weekDate >= rangeStart && weekDate <= rangeEnd;

    const entries = Array.isArray(week.entries) ? week.entries : [];
    entries.forEach((projectEntry) => {
      const flowId = projectEntry.flowId || '';
      const projectFromMap = projectsMap[flowId];
      const projectName = getProjectName(projectEntry, projectFromMap);
      const projectState = projectFromMap?.state || '';
      const projectIsNonBillable = toBool(projectFromMap?.isNonBillable);
      const dashboardTreatsAsNonBillable =
        projectIsNonBillable || NON_BILLABLE_PROJECT_STATES.includes(projectState);
      const isPaidLeave =
        flowId === PAID_LEAVE_FLOW_ID || projectName === PAID_LEAVE_PROJECT_NAME;

      const hours = Array.isArray(projectEntry.hours) ? projectEntry.hours : [];
      hours.forEach((entry) => {
        const entryDate = parseEntryDate(entry.date);
        const dateInRange =
          entryDate && entryDate >= rangeStart && entryDate <= rangeEnd;
        const includedInDashboard =
          weekIncluded && dateInRange && !isPaidLeave && !isContractUser;
        if (!includedInDashboard) return;
        // Match the dashboard's truthy check exactly (utilization-utils.js:60).
        const entryIsNonBillable = !!entry.isNonBillable;

        rows.push({
          weekStarting,
          weekIncluded,
          includedInDashboard,
          flowId,
          projectName,
          date: entry.date || '',
          hours: parseFloat(entry.hours || 0) || 0,
          isNonBillableOnTimesheet: entryIsNonBillable,
          stateOnTimesheet: entry.state || '',
          projectState,
          projectIsNonBillable,
          dashboardTreatsAsNonBillable,
        });
      });
    });
  });

  return rows;
};

// Build an array-of-arrays (header row + data rows) for the raw-entries sheet.
const buildEntriesAoa = (rows) => {
  const headers = [
    'weekStarting',
    'weekIncluded',
    'includedInDashboard',
    'flowId',
    'projectName',
    'date',
    'hours',
    'isNonBillableOnTimesheet',
    'stateOnTimesheet',
  ];
  const dataRows = rows.map((r) => [
    r.weekStarting,
    r.weekIncluded,
    r.includedInDashboard,
    r.flowId,
    r.projectName,
    r.date,
    round2(r.hours),
    r.isNonBillableOnTimesheet,
    r.stateOnTimesheet,
  ]);
  return [headers, ...dataRows];
};

const buildAggregateAoa = (rows, user) => {
  const serviceGroup = user?.serviceGroup || 'Unknown';
  const userName = user?.fullName || `${user?.firstName || ''} ${user?.surname || ''}`.trim();

  const byFlowId = new Map();
  rows.forEach((r) => {
    if (!r.includedInDashboard) return;
    if (!byFlowId.has(r.flowId)) {
      byFlowId.set(r.flowId, {
        flowId: r.flowId,
        projectName: r.projectName,
        billableHours: 0,
        nonBillableHours: 0,
        projectState: r.projectState,
        projectIsNonBillable: r.projectIsNonBillable,
        dashboardTreatsAsNonBillable: r.dashboardTreatsAsNonBillable,
      });
    }
    const agg = byFlowId.get(r.flowId);
    if (r.dashboardTreatsAsNonBillable) {
      agg.nonBillableHours += r.hours;
    } else if (r.isNonBillableOnTimesheet) {
      agg.nonBillableHours += r.hours;
    } else {
      agg.billableHours += r.hours;
    }
  });

  const headers = [
    'serviceGroup',
    'userName',
    'flowId',
    'projectName',
    'billableHours',
    'nonBillableHours',
    'projectState',
    'projectIsNonBillable',
    'dashboardTreatsAsNonBillable',
  ];
  const dataRows = Array.from(byFlowId.values())
    .sort((a, b) => (a.projectName || '').localeCompare(b.projectName || ''))
    .map((p) => [
      serviceGroup,
      userName,
      p.flowId,
      p.projectName,
      round2(p.billableHours),
      round2(p.nonBillableHours),
      p.projectState,
      p.projectIsNonBillable,
      p.dashboardTreatsAsNonBillable,
    ]);
  return [headers, ...dataRows];
};

const slugify = (value) =>
  String(value || 'user')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .toLowerCase();

export const downloadUserTimesheetWorkbook = async ({ user, startDate, endDate }) => {
  if (!user) throw new Error('No user provided');
  if (!startDate || !endDate) throw new Error('Date range is required');

  const timesheetDocId = getTimesheetDocId(user);
  const isContractUser = user.employmentStatus === 'Contract';

  const [weeks, projectsArr] = await Promise.all([fetchUserWeeks(timesheetDocId), fetchProjects()]);

  const projectsMap = (projectsArr || []).reduce((acc, p) => {
    if (p?.id) acc[p.id] = p;
    return acc;
  }, {});

  const rows = buildEnrichedRows({
    weeks,
    projectsMap,
    startDate,
    endDate,
    isContractUser,
  });

  const entriesSheet = XLSX.utils.aoa_to_sheet(buildEntriesAoa(rows));
  const aggregateSheet = XLSX.utils.aoa_to_sheet(buildAggregateAoa(rows, user));

  const workbook = XLSX.utils.book_new();
  // Sheet names are capped at 31 chars and may not contain : \ / ? * [ ].
  XLSX.utils.book_append_sheet(workbook, entriesSheet, 'Entries');
  XLSX.utils.book_append_sheet(workbook, aggregateSheet, 'By Project');

  const userSlug = slugify(user.fullName || `${user.firstName}_${user.surname}`);
  const startStr = dayjs(startDate).format('YYYY-MM-DD');
  const endStr = dayjs(endDate).format('YYYY-MM-DD');

  // writeFile builds the .xlsx and triggers the browser download in one step.
  XLSX.writeFile(workbook, `timesheet-${userSlug}-${startStr}-to-${endStr}.xlsx`);

  return { entryCount: rows.length };
};
